// Output sinks for exports: positional writes go either straight to a file the
// user picked (File System Access — any size, little memory, written atomically:
// the browser commits the file only on close) or into an in-memory sparse buffer
// that becomes a Blob for download.

export interface ExportSink {
  /** Write bytes at an absolute file position (muxers patch headers later). */
  write(data: Uint8Array, position: number): void;
  /** Wait for queued writes (call now and then for backpressure). */
  drain(): Promise<void>;
  /** Finish: resolves to the Blob (download sink) or null (file sink, already on disk). */
  close(): Promise<Blob | null>;
  /** Discard a failed/cancelled export (a file sink leaves no partial file). */
  abort(): Promise<void>;
  /** The written file, for post-export verification. */
  result(): Promise<Blob | null>;
}

/** In-memory sink that tolerates out-of-order and overlapping writes. */
export class BlobSink implements ExportSink {
  private segs: { pos: number; data: Uint8Array }[] = [];
  private blob: Blob | null = null;
  constructor(private type: string) {}
  write(data: Uint8Array, position: number) {
    // Patch any existing bytes this write overlaps; keep the rest as new segments.
    let rest: { pos: number; data: Uint8Array }[] = [{ pos: position, data: data.slice() }];
    for (const s of this.segs) {
      const next: typeof rest = [];
      for (const r of rest) {
        const a = Math.max(r.pos, s.pos), b = Math.min(r.pos + r.data.length, s.pos + s.data.length);
        if (a >= b) { next.push(r); continue; }
        s.data.set(r.data.subarray(a - r.pos, b - r.pos), a - s.pos);
        if (r.pos < a) next.push({ pos: r.pos, data: r.data.subarray(0, a - r.pos) });
        if (r.pos + r.data.length > b) next.push({ pos: b, data: r.data.subarray(b - r.pos) });
      }
      rest = next;
      if (!rest.length) break;
    }
    this.segs.push(...rest);
  }
  async drain() {}
  async close() {
    this.segs.sort((x, y) => x.pos - y.pos);
    const parts: BlobPart[] = [];
    let at = 0;
    for (const s of this.segs) {
      if (s.pos > at) parts.push(new Uint8Array(s.pos - at)); // never-written gap → zeros
      parts.push(s.data as BlobPart);
      at = Math.max(at, s.pos + s.data.length);
    }
    this.segs = [];
    this.blob = new Blob(parts, { type: this.type });
    return this.blob;
  }
  async abort() { this.segs = []; }
  async result() { return this.blob; }
}

/** Sink writing to a user-chosen file via the File System Access API. */
export class FileSink implements ExportSink {
  private queue: Promise<void> = Promise.resolve();
  private failed: unknown = null;
  private constructor(private handle: any, private writable: any) {}
  static async open(handle: any): Promise<FileSink> {
    return new FileSink(handle, await handle.createWritable({ keepExistingData: false }));
  }
  write(data: Uint8Array, position: number) {
    const copy = data.slice();
    this.queue = this.queue.then(() => this.writable.write({ type: "write", position, data: copy })).catch((e) => { this.failed ??= e; });
  }
  private closed = false;
  /** Write a whole finished Blob (e.g. a ZIP built in parts) at the start of the file. */
  async writeBlob(blob: Blob) { await this.drain(); await this.writable.write({ type: "write", position: 0, data: blob }); }
  async drain() { await this.queue; if (this.failed) throw this.failed; }
  async close() { if (this.closed) return null; this.closed = true; await this.drain(); await this.writable.close(); return null; }
  async abort() { if (this.closed) return; this.closed = true; try { await this.queue; } catch {} try { await this.writable.abort(); } catch {} }
  async result() { try { return await this.handle.getFile(); } catch { return null; } }
}

export const canPickSaveFile = () => typeof (window as any).showSaveFilePicker === "function";

/** Ask where to save (must run inside a user gesture). null = unsupported;
 *  throws AbortError if the user cancels. */
export async function pickSaveFile(suggestedName: string, description: string, mime: string, ext: string): Promise<any | null> {
  if (!canPickSaveFile()) return null;
  return (window as any).showSaveFilePicker({ suggestedName, types: [{ description, accept: { [mime]: [ext] } }] });
}

/** Walk an MP4's top-level boxes: the file must be a sequence of whole boxes and
 *  contain ftyp, moov and mdat. Returns a problem description, or null if OK. */
export async function checkMp4(file: Blob): Promise<string | null> {
  let pos = 0;
  const seen = new Set<string>();
  while (pos < file.size) {
    if (file.size - pos < 8) return "trailing bytes after the last box";
    const h = new DataView(await file.slice(pos, pos + 16).arrayBuffer());
    let size = h.getUint32(0);
    const type = String.fromCharCode(h.getUint8(4), h.getUint8(5), h.getUint8(6), h.getUint8(7));
    if (size === 1) size = Number(h.getBigUint64(8));
    else if (size === 0) size = file.size - pos; // box runs to end of file
    if (size < 8 || pos + size > file.size) return `box "${type}" at ${pos} has an invalid size`;
    seen.add(type);
    pos += size;
  }
  for (const t of ["ftyp", "moov", "mdat"]) if (!seen.has(t)) return `missing "${t}" box`;
  return null;
}
